import { Box, Typography, useTheme } from '@mui/material';
import { type SeatUsagePoint } from '@store/api/activationLogEndpoints';

/**
 * Seats in use across the selected window, drawn small.
 *
 * Not a full chart on purpose — it sits above the list as a heading, to answer "when was it
 * busy" at a glance and point the operator at the hour worth filtering to. Inline SVG rather
 * than a charting dependency: two polylines and a ceiling is the whole requirement.
 */
const UsageSparkline = ({ points, limit }: { points: SeatUsagePoint[], limit: number }) => {
  const theme = useTheme();

  if (points.length < 2) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>
        Not enough samples yet. Usage is recorded every 15 minutes.
      </Typography>
    );
  }

  const width = 1000;
  const height = 90;
  // The ceiling is drawn from the entitlement, so a flat line near the top reads as "full"
  // without anybody having to know the number.
  const ceiling = Math.max(limit, ...points.map(point => point.supar + point.composer), 1);

  const x = (index: number) => (index / (points.length - 1)) * width;
  const y = (value: number) => height - (value / ceiling) * height;

  const line = (pick: (point: SeatUsagePoint) => number) =>
    points.map((point, index) => `${x(index).toFixed(1)},${y(pick(point)).toFixed(1)}`).join(' ');

  const first = new Date(points[0].at);
  const last = new Date(points[points.length - 1].at);

  return (
    <Box>
      <Box
        component='svg'
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio='none'
        sx={{ width: '100%', height: 90, display: 'block' }}
        role='img'
        aria-label={`Seats in use between ${first.toLocaleString()} and ${last.toLocaleString()}`}
      >
        <line
          x1={0} x2={width} y1={y(limit)} y2={y(limit)}
          stroke={theme.palette.error.main} strokeDasharray='6 6' strokeWidth={1.5} vectorEffect='non-scaling-stroke'
        />
        <polyline
          points={line(point => point.supar + point.composer)}
          fill='none' stroke={theme.palette.primary.main} strokeWidth={2} vectorEffect='non-scaling-stroke'
        />
        <polyline
          points={line(point => point.composer)}
          fill='none' stroke={theme.palette.secondary.main} strokeWidth={1.5}
          strokeDasharray='4 4' vectorEffect='non-scaling-stroke'
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant='caption' color='text.secondary'>{first.toFormattedDateTimeString()}</Typography>
        <Typography variant='caption' color='text.secondary'>
          total &mdash;&nbsp;&nbsp;composer ---&nbsp;&nbsp;limit {limit}
        </Typography>
        <Typography variant='caption' color='text.secondary'>{last.toFormattedDateTimeString()}</Typography>
      </Box>
    </Box>
  );
};

export default UsageSparkline;
